import TipTapRenderer from '../components/TipTapRenderer';
import { GetReservation200 } from '../../openapi/model';
import { useLang } from '../hooks/useLanguage';
import { useCallback, useState } from 'react';
import { cancelBooking } from '../../openapi/bookings/bookings';
import { useMarketing } from '../hooks/useMarketing.tsx';

function CancellationPolicy({
    reservation,
    reservationId,
    reloadOrdersAndReservation,
    setShowCancelInformations,
    reservationHash,
    canBeCancelled,
    paidValue,
}: {
    reservation: GetReservation200;
    reservationId: number;
    reloadOrdersAndReservation: () => void;
    setShowCancelInformations: (show: boolean) => void;
    reservationHash: string;
    canBeCancelled: boolean;
    paidValue: number;
}) {
    const { t, getI18n } = useLang();
    const { refund } = useMarketing();
    const [showVenueContact, setShowVenueContact] = useState(false);

    const now = new Date();
    const cancelTime = new Date(reservation.startTime);
    cancelTime.setHours(cancelTime.getHours() - reservation.cancelWindow);
    const isBeforeCancelWindow = now < cancelTime;

    const cancelReservation = useCallback(async () => {
        try {
            await cancelBooking(reservationId, { reservationHash });
            // A cancel before the cancel window is a full refund — mirror it to
            // GA4 with the standard `refund` event so reported revenue/ROAS net
            // out. Reuse the purchase transaction_id so GA4 reverses the right
            // transaction. Cancels after the window return no money, so we don't
            // emit a refund. (Tracking must never block the cancel itself.)
            if (isBeforeCancelWindow && paidValue > 0) {
                refund({
                    value: paidValue,
                    currency: 'USD',
                    transaction_id: `res-${reservationId}`,
                });
            }
            reloadOrdersAndReservation();
            setShowCancelInformations(false);
        } catch (error) {
            console.error('Error cancelling booking:', error);
            alert(t('errors.cancelReservationError'));
            return;
        }
    }, [
        reservationId,
        reservationHash,
        t,
        reloadOrdersAndReservation,
        setShowCancelInformations,
        isBeforeCancelWindow,
        paidValue,
        refund,
    ]);
    const confirmButtonText = isBeforeCancelWindow
        ? t('bookings.cancelBookingFullRefund')
        : t('bookings.cancelBookingWithNoRefund');

    const handleContactVenue = () => {
        setShowVenueContact(true);
    };

    const handleCloseVenueContact = () => {
        setShowVenueContact(false);
    };

    return (
        <div className="fireknife-booking-cancellation">
            <h2 className="fireknife-booking-cancellation__title">
                {t('widgetBookingSummary.cancellationPolicy')}
            </h2>
            <div className="fireknife-booking-cancellation__desc">
                <TipTapRenderer
                    content={getI18n(
                        reservation?.cancelDescription,
                        reservation?.cancelDescriptionI18n,
                    )}
                />
            </div>
            {canBeCancelled && (
                <div className="fireknife-cancellation-info__btn-box">
                    <button
                        className="fireknife-cancellation-info__cancel-btn fireknife-btn fireknife-btn--primary"
                        onClick={cancelReservation}
                        type="button"
                    >
                        {confirmButtonText}
                    </button>
                    {!isBeforeCancelWindow && (
                        <button
                            className="fireknife-cancellation-info__contact-btn fireknife-btn fireknife-btn--link"
                            onClick={handleContactVenue}
                            type="button"
                        >
                            {t('bookings.contactVenue')}
                        </button>
                    )}
                </div>
            )}
            {showVenueContact && (
                <div className="fireknife-cancellation-info__venue-contact-modal">
                    <div className="fireknife-cancellation-info__venue-contact-content">
                        <h3>{t('bookings.contactVenueTitle')}</h3>
                        <p>{t('bookings.contactVenueMessage')}</p>
                        <div className="fireknife-cancellation-info__contact-details">
                            <p>
                                <strong>{t('bookings.bookingId')}:</strong> {reservationId}
                            </p>
                            {reservation.venueEmail && (
                                <p>
                                    <strong>{t('bookings.email')}:</strong> {reservation.venueEmail}
                                </p>
                            )}
                            {reservation.venuePhone && (
                                <p>
                                    <strong>{t('bookings.phone')}:</strong> {reservation.venuePhone}
                                </p>
                            )}
                        </div>
                        <div className="fireknife-cancellation-info__btn-box">
                            <button
                                className="fireknife-cancellation-info__close-btn fireknife-btn fireknife-btn--primary"
                                onClick={handleCloseVenueContact}
                                type="button"
                            >
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CancellationPolicy;
